// Deploy safety, tab side (docs/ARCHITECTURE-PROPOSAL.md §7.2). The site is deployed many times a day while learners
// have it open. The tab knows the build it was loaded from (<meta name="build">) and checks /version.json every
// ten minutes and when it becomes visible again. It never reloads mid-task, and never mid-journey (#172):
// - a changed part (a project, home = intro, lab and city, or a site) is loaded fresh at the next safe point: a move between
//   the world and a project while nothing is playing. The world (intro, lab, city, sites) is one journey: the camera and the
//   music carry on between its views, so it is never reloaded between them;
// - a changed shell (router, engine, storage) the same way;
// - until then the tab runs its own build's code, which the site keeps for 14 days (§7.1); a file gone after all is the
//   shell's reload-once safety net;
// - a newer save format stops this tab from saving (the view tells the learner to reload).
import type { Progress } from "@youbuild/progress";

export interface BuildInfo { build: string; minFormat: number; parts: Record<string, string> }

export function readOwnBuild(): BuildInfo | null {
  try { return JSON.parse(document.querySelector<HTMLMetaElement>('meta[name="build"]')?.content ?? "null"); } catch { return null; }
}

/** Parts whose fingerprint differs; a part the live site no longer has counts as changed. */
export function changedParts(own: BuildInfo, live: BuildInfo): Set<string> {
  const out = new Set<string>();
  for (const [part, fp] of Object.entries(own.parts)) if (live.parts[part] !== fp) out.add(part);
  return out;
}

/** The world: the intro, the lab and the city (home) and every site. One journey, never reloaded inside. */
export const inWorld = (part: string) => part === "home" || part.startsWith("SITE-");
/** Whether a move from one part to another loads the page fresh: only when the shell or the part it opens changed, nothing is
 *  playing (the music never stops for a deploy), and it is not a move inside the world. The first view of a page (from null)
 *  is already fresh. */
export function freshLoad(stale: ReadonlySet<string>, from: string | null, to: string, playing: boolean): boolean {
  if (from === null || playing) return false;
  if (!stale.has("shell") && !stale.has(to)) return false;
  return !(inWorld(from) && inWorld(to));
}

export function watchDeploys({ progress, formatNow, every = 10 * 60e3 }: { progress: Progress; formatNow: number; every?: number }) {
  const own = readOwnBuild();
  let stale = new Set<string>();
  async function check() {
    if (!own) return;                                    // dev server: nothing to compare
    try {
      const r = await fetch("/version.json", { cache: "no-store" });
      if (!r.ok) return;
      const live: BuildInfo = await r.json();
      stale = changedParts(own, live);
      if (live.minFormat > formatNow) progress.block();
    } catch { /* offline: try again later */ }
  }
  if (own) {
    setInterval(check, every);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void check(); });
  }
  return {
    check,
    /** True when moving from one part to another should load the page fresh instead of moving inside it (freshLoad). */
    needsFreshLoad: (to: string, from: string | null = null, playing = false) => freshLoad(stale, from, to, playing)
  };
}
